import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, ClipboardCheck, CheckCircle, XCircle, User, FileText, Printer, Camera, Upload, Image, Film, Calendar, BarChart3, Lock, ExternalLink, MapPin, Download, Clock } from 'lucide-react';
import API, { getPM, getPMExport, createPM, updatePM, deletePM, getVehicles, getPenugasan, getCM } from '../api';
import { useAuth } from '../AuthContext';

const KONDISI_OPTIONS = ['Baik', 'Tidak'];

// ====== Helper: Kompresi foto ke base64 (max 800px, quality 70%) ======
const compressImage = (file, maxWidth = 800, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round(height * maxWidth / width);
          width = maxWidth;
        }
        if (height > maxWidth) {
          width = Math.round(width * maxWidth / height);
          height = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Gagal memuat gambar'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Gagal membaca file'));
    reader.readAsDataURL(file);
  });
};

// ====== Helper: Waktu sekarang dalam WIB (Asia/Jakarta) ======
const getWIBDateTime = () => {
  const now = new Date();
  const f = new Intl.DateTimeFormat('en', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = f.formatToParts(now);
  const get = (t) => parts.find(p => p.type === t)?.value || '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

const DEFAULT_CHECKLIST = [
  // 1. CHECKLIST OBU
  { kategori: 'CHECKLIST OBU', nama: 'Tampilan dashboard obu', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST OBU', nama: 'Layar obu menyala normal (brightness, touch screen)', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST OBU', nama: 'Obu Fungsi Tulis ke Runningtext bekerja', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST OBU', nama: 'Running Text Status terbaca (device connected) obu', kondisi: 'Baik', checked: false, keterangan: '' },
  // 2. CHECKLIST RUNNING TEXT
  { kategori: 'CHECKLIST RUNNING TEXT', nama: 'Modul LED menyala normal', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST RUNNING TEXT', nama: 'Teks terbaca jelas siang & malam', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST RUNNING TEXT', nama: 'Perintah dari OBU terbaca', kondisi: 'Baik', checked: false, keterangan: '' },
  // 3. CHECKLIST MDVR & CCTV
  { kategori: 'CHECKLIST MDVR & CCTV', nama: 'Kamera depan berfungsi', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST MDVR & CCTV', nama: 'Kamera kabin berfungsi', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST MDVR & CCTV', nama: 'Kamera arah pramudi berfungsi', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST MDVR & CCTV', nama: 'Kamera belakang berfungsi', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST MDVR & CCTV', nama: 'Penyimpanan Merekam/Record', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST MDVR & CCTV', nama: 'Monitor CCTV', kondisi: 'Baik', checked: false, keterangan: '' },
  // 4. CHECKLIST PERANGKAT TAMBAHAN
  { kategori: 'CHECKLIST PERANGKAT TAMBAHAN', nama: 'AC Depan', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST PERANGKAT TAMBAHAN', nama: 'AC Belakang', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST PERANGKAT TAMBAHAN', nama: 'Pintu otomatis', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST PERANGKAT TAMBAHAN', nama: 'Buzzer Mundur jika terpasang', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST PERANGKAT TAMBAHAN', nama: 'Mikrofon/speaker', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST PERANGKAT TAMBAHAN', nama: 'TV monitor Layanan', kondisi: 'Baik', checked: false, keterangan: '' },
  { kategori: 'CHECKLIST PERANGKAT TAMBAHAN', nama: 'Kamera mundur jika terpasang', kondisi: 'Baik', checked: false, keterangan: '' },
];

// ====== MultiFileUpload Component ======
function MultiFileUpload({ label, fileType, urls, uploading, onUpload, onRemove }) {
  const inputRef = React.useRef(null);
  const isFoto = fileType === 'foto';
  const urlList = urls || [];

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    onUpload(files);
    e.target.value = '';
  };

  return (
    <div className="form-group">
      <label>{label} {urlList.length > 0 && <span style={{ fontSize: '0.7rem', color: '#2dce89', fontWeight: 600 }}>({urlList.length} file)</span>}</label>
      <div style={{
        border: '2px dashed var(--gray-200)',
        borderRadius: 8, padding: 12,
        background: urlList.length > 0 ? '#f0fdf4' : '#fafafa',
        transition: 'all 0.2s',
      }}>
        {/* Thumbnail Gallery */}
        {urlList.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {urlList.map((url, idx) => (
              <div key={idx} style={{ position: 'relative', width: 90, height: 70 }}>
                {isFoto ? (
                  <img src={url} alt={`Foto ${idx+1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%', borderRadius: 6,
                    background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid var(--gray-200)',
                  }}>
                    <Film size={18} color="#fff" />
                  </div>
                )}
                <button type="button" onClick={() => onRemove(idx)}
                  style={{
                    position: 'absolute', top: -4, right: -4,
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#f5365c', color: '#fff', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}>
                  <X size={10} />
                </button>
                <a href={url} target="_blank" rel="noopener noreferrer"
                  style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    fontSize: '0.55rem', color: '#fff', textAlign: 'center',
                    background: 'rgba(0,0,0,0.5)', padding: '1px 4px',
                    borderRadius: '0 0 6px 6px', textDecoration: 'none',
                  }}>
                  {url.split('/').pop().substring(0, 15)}
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        <label style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 6, padding: urlList.length > 0 ? '8px' : '16px 8px', cursor: 'pointer',
          color: 'var(--gray-400)', transition: 'all 0.15s',
          border: urlList.length > 0 ? '1px dashed var(--gray-200)' : 'none',
          borderRadius: 6,
        }}
          onMouseEnter={e => e.currentTarget.style.color = '#2dce89'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-400)'}>
          {uploading ? (
            <div style={{ textAlign: 'center' }}>
              <div className="spinner" style={{
                width: 24, height: 24, border: '2px solid var(--gray-200)',
                borderTopColor: '#2dce89', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite', margin: '0 auto 6px',
              }} />
              <span style={{ fontSize: '0.75rem' }}>Uploading {urlList.length + 1}...</span>
            </div>
          ) : (
            <>
              <Upload size={20} />
              <span style={{ fontSize: '0.78rem' }}>Tambah {isFoto ? 'Foto' : 'Video'}</span>
              <span style={{ fontSize: '0.65rem' }}>Klik untuk pilih (bisa multi-file)</span>
            </>
          )}
          <input ref={inputRef} type="file" hidden
            accept={isFoto ? 'image/*' : 'video/*'}
            multiple
            onChange={handleFileSelect}
          />
        </label>
      </div>
    </div>
  );
}

const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    borderBottom: '2px solid #2dce89',
    paddingBottom: 8, marginBottom: 16, marginTop: 20,
  }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8,
      background: 'linear-gradient(135deg, #2dce89, #1aae6f)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff',
    }}>
      {icon}
    </div>
    <div>
      <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>{title}</h4>
      {subtitle && <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>{subtitle}</small>}
    </div>
  </div>
);

const FieldGroup = ({ cols, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: cols || '1fr 1fr', gap: 12, marginBottom: 4 }}>
    {children}
  </div>
);

const LabelInput = ({ label, required, children }) => (
  <div className="form-group">
    <label>{label}{required && <span style={{ color: '#e74c3c' }}> *</span>}</label>
    {children}
  </div>
);
const emptyForm = {
  vehicle_id: '', penugasan_id: '', no_body: '', no_polisi: '', rute: '', jenis_ac: '', odometer: '',
  teknisi: '', pengawas: '', tanggal: '', checklist_items: DEFAULT_CHECKLIST, 
  foto_url: '', video_url: '', foto_urls: [], video_urls: [], catatan: '', lokasi: '', status: 'Selesai',
  ttd_pengawas: '', ttd_teknisi: '', ttd_spv: '',
};

// ====== Helper: periode penugasan yang masih berlaku ======
// PM ber-status 'Selesai' hanya dianggap menyelesaikan kendaraan jika tanggal PM berada
// DALAM rentang penugasan teknisi yang masih berlaku (belum berakhir). PM historis bulan
// lalu TIDAK boleh menyembunyikan kendaraan pada penugasan periode berikutnya.
const getTodayStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const getActivePenugasanPeriods = (penugasanData, teknisiName) => {
  const todayStr = getTodayStr();
  return (penugasanData || [])
    .filter(p => p.karyawan_nama?.toLowerCase() === String(teknisiName || '').toLowerCase())
    .filter(p => {
      const end = String(p.tanggal_selesai || '').slice(0, 10);
      // Penugasan tanpa tanggal selesai dianggap masih berlaku
      return !end || end >= todayStr;
    });
};

// Apakah tanggal PM (YYYY-MM-DD) jatuh dalam salah satu periode penugasan?
const pmFallsInPeriod = (pmDate, periods) => {
  if (!pmDate) return false;
  return periods.some(p => {
    const start = String(p.tanggal_mulai || '').slice(0, 10);
    const end = String(p.tanggal_selesai || '').slice(0, 10);
    if (start && pmDate < start) return false;
    if (end && pmDate > end) return false;
    return true;
  });
};

// ====== PMModal Component (fully isolated form state) ======
function PMModal({ editing, teknisiPenugasan, penugasanData, vehicles, allPMData, onClose, onSaved, user }) {
  const [form, setForm] = useState(() => {
    if (editing) {
      const item = editing;
      const fotoUrls = item.foto_urls || (item.foto_url ? [item.foto_url] : []);
      const videoUrls = item.video_urls || (item.video_url ? [item.video_url] : []);
      return {
        vehicle_id: item.vehicle_id ?? '',
        penugasan_id: item.penugasan_id ?? '',
        no_body: item.no_body || '',
        no_polisi: item.no_polisi || '',
        rute: item.rute || '',
        jenis_ac: item.jenis_ac || '',
        lokasi: item.lokasi || '',
        odometer: item.odometer ?? '',
        teknisi: item.teknisi || '',
        pengawas: item.pengawas || '',
        tanggal: item.tanggal?.slice(0, 16) || '',
        checklist_items: item.checklist_items?.length > 0
          ? item.checklist_items.map(ci => ({ ...ci, checked: ci.checked === true, keterangan: ci.keterangan || '' }))
          : DEFAULT_CHECKLIST,
        foto_url: item.foto_url || '',
        video_url: item.video_url || '',
        foto_urls: fotoUrls,
        video_urls: videoUrls,
        catatan: item.catatan || '',
        status: item.status || 'Direncanakan',
        ttd_pengawas: item.ttd_pengawas || '',
        ttd_teknisi: item.ttd_teknisi || '',
        ttd_spv: item.ttd_spv || '',
      };
    }
    // Auto-select teknisi if current user has role teknisi
    const isTeknisi = user?.role?.toLowerCase() === 'teknisi';
    let defaultTeknisi = '';
    if (isTeknisi && teknisiPenugasan?.length > 0) {
      const found = teknisiPenugasan.find(t => t.nama?.toLowerCase() === (user?.full_name || user?.username || '').toLowerCase());
      if (found) defaultTeknisi = found.nama;
    }
    return {
      ...emptyForm,
      teknisi: defaultTeknisi,
      tanggal: getWIBDateTime(),
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState({ foto: false, video: false });

  const updateChecklistItem = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleVehicleSelect = (e) => {
    const vid = e.target.value;
    setForm(prev => {
      if (vid) {
        const v = vehicles.find(v => v.id === Number(vid));
        if (v) {
          const isAC = v.jenis_ac === 'AC';
          // Filter checklist berdasarkan jenis AC
          let updatedChecklist = [...prev.checklist_items];
          if (!isAC) {
            // Non AC: hanya tampilkan 2 item untuk PERANGKAT TAMBAHAN
            updatedChecklist = prev.checklist_items.map(ci => {
              if (ci.kategori === 'CHECKLIST PERANGKAT TAMBAHAN') {
                const allowedNames = ['Buzzer Mundur jika terpasang', 'Kamera mundur jika terpasang'];
                if (!allowedNames.includes(ci.nama)) {
                  return { ...ci, _hidden: true };
                }
              }
              return ci;
            });
          } else {
            // AC: tampilkan semua, hapus _hidden
            updatedChecklist = prev.checklist_items.map(ci => ({ ...ci, _hidden: false }));
          }
          return { ...prev, vehicle_id: vid, no_body: v.body_no || '', no_polisi: v.plate_no || '', rute: v.route_code || v.trayek_no || '', jenis_ac: v.jenis_ac || '', lokasi: v.pengendapan || '', checklist_items: updatedChecklist };
        }
      }
      return { ...prev, vehicle_id: '', no_body: '', no_polisi: '' };
    });
  };

  // Compute available vehicles dynamically based on selected teknisi in the form
  const getFilteredVehicles = useCallback((teknisiName, editingVehicleId) => {
    if (!teknisiName) return [];
    // Hanya penugasan yang masih berlaku (belum berakhir) — data PM mengacu pada penugasan periode berjalan
    const activePeriods = getActivePenugasanPeriods(penugasanData, teknisiName);
    const assignedIds = new Set(
      activePeriods.flatMap(p => p.vehicle_data || []).map(v => v.id)
    );
    // PM Selesai hanya dihitung jika terjadi DALAM rentang penugasan aktif teknisi,
    // bukan PM historis dari bulan/penugasan sebelumnya.
    const completedIds = new Set(
      allPMData
        .filter(pm => pm.teknisi?.toLowerCase() === teknisiName.toLowerCase()
          && pm.status === 'Selesai'
          && pmFallsInPeriod(String(pm.tanggal || '').slice(0, 10), activePeriods))
        .map(pm => pm.vehicle_id)
        .filter(Boolean)
    );
    return vehicles.filter(v => {
      if (!assignedIds.has(v.id)) return false;
      // Always include the vehicle being edited, even if it has completed PM
      if (editingVehicleId && Number(v.id) === Number(editingVehicleId)) return true;
      if (completedIds.has(v.id)) return false;
      return true;
    });
  }, [penugasanData, vehicles, allPMData]);

  const filteredVehicles = getFilteredVehicles(form.teknisi, editing?.vehicle_id);
  const totalAssigned = form.teknisi
    ? new Set(getActivePenugasanPeriods(penugasanData, form.teknisi)
        .flatMap(p => p.vehicle_data || []).map(v => v.id)
      ).size
    : 0;

  const onTeknisiFormChange = (nama) => {
    setForm(prev => ({ ...prev, teknisi: nama, vehicle_id: '', no_body: '', no_polisi: '', rute: '', jenis_ac: '', lokasi: '' }));
    // Auto-select first available vehicle with checklist filtering
    if (nama) {
      const vehList = getFilteredVehicles(nama);
      if (vehList.length > 0) {
        const v = vehList[0];
        const isAC = v.jenis_ac === 'AC';
        const updatedChecklist = DEFAULT_CHECKLIST.map(ci => {
          if (!isAC && ci.kategori === 'CHECKLIST PERANGKAT TAMBAHAN') {
            const allowedNames = ['Buzzer Mundur jika terpasang', 'Kamera mundur jika terpasang'];
            if (!allowedNames.includes(ci.nama)) {
              return { ...ci, _hidden: true };
            }
          }
          return { ...ci, _hidden: false };
        });
        setForm(f => ({
          ...f,
          vehicle_id: v.id,
          no_body: v.body_no || '',
          no_polisi: v.plate_no || '',
          rute: v.route_code || v.trayek_no || '',
          jenis_ac: v.jenis_ac || '',
          lokasi: v.pengendapan || '',
          checklist_items: updatedChecklist,
        }));
      }
    }
  };

  const handleMultiUpload = async (files, fileType) => {
    if (!files || files.length === 0) return;
    setUploading(prev => ({ ...prev, [fileType]: true }));
    try {
      // === FOTO: kompres → base64 langsung, simpan ke DB ===
      if (fileType === 'foto') {
        const base64Promises = Array.from(files).map(f => compressImage(f, 800, 0.7));
        const base64Urls = await Promise.all(base64Promises);
        setForm(prev => {
          const existing = prev.foto_urls || [];
          return { ...prev, foto_urls: [...existing, ...base64Urls] };
        });
      } else {
        // === VIDEO: tetap upload file ke server ===
        const formData = new FormData();
        Array.from(files).forEach(f => formData.append('files', f));
        formData.append('type', fileType);
        const res = await API.post('/pm/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const newUrls = res.data.urls || (res.data.url ? [res.data.url] : []);
        setForm(prev => {
          const key = 'video_urls';
          const existing = prev[key] || [];
          return { ...prev, [key]: [...existing, ...newUrls] };
        });
      }
    } catch (err) {
      setError(`Gagal upload ${fileType}: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleRemoveFile = (idx, fileType) => {
    setForm(prev => {
      const key = fileType === 'foto' ? 'foto_urls' : 'video_urls';
      const updated = (prev[key] || []).filter((_, i) => i !== idx);
      return { ...prev, [key]: updated };
    });
  };

  const handleSave = async () => {
    setError('');
    if (!form.tanggal) { setError('Tanggal harus diisi'); return; }
    if (!form.teknisi) { setError('Nama teknisi harus diisi'); return; }
    const visibleItems = form.checklist_items.filter(i => !i._hidden);
    const uncheckedItems = visibleItems.filter(i => !i.checked);
    if (uncheckedItems.length > 0) {
      const shown = uncheckedItems.slice(0, 5).map(i => i.nama).join(', ');
      const suffix = uncheckedItems.length > 5 ? `, ...dan ${uncheckedItems.length - 5} item lainnya` : '';
      setError(`Ada ${uncheckedItems.length} item checklist yang belum dicentang: ${shown}${suffix}`);
      return;
    }
    setSaving(true);
    try {
        const allChecked = visibleItems.every(i => i.checked);
        const payload = {
          ...form,
          status: allChecked ? 'Selesai' : 'Direncanakan',
          tanggal: form.tanggal.includes('T') ? form.tanggal + ':00' : form.tanggal,
          vehicle_id: form.vehicle_id ? Number(form.vehicle_id) : null,
          penugasan_id: form.penugasan_id ? Number(form.penugasan_id) : null,
          odometer: form.odometer ? Number(form.odometer) : null,
          foto_urls: form.foto_urls || [],
          video_urls: form.video_urls || [],
          foto_url: (form.foto_urls && form.foto_urls.length > 0) ? form.foto_urls[0] : (form.foto_url || ''),
          video_url: (form.video_urls && form.video_urls.length > 0) ? form.video_urls[0] : (form.video_url || ''),
        };
      if (editing) {
        await updatePM(editing.id, payload);
      } else {
        await createPM(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()}
        style={{
          maxWidth: 860, width: '95%', maxHeight: '90vh', overflow: 'auto',
          borderRadius: 12,
        }}>
        <div className="modal-header" style={{
          background: 'linear-gradient(135deg, #2dce89, #1aae6f)',
          color: '#fff', borderRadius: '12px 12px 0 0', padding: '16px 24px',
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: '1.1rem' }}>
              {editing ? 'Edit PM' : 'Form Preventive Maintenance (PM)'}
            </h3>
            <small style={{ opacity: 0.85 }}>Pemeliharaan Berkala · {new Date().toLocaleDateString('id-ID')}</small>
          </div>
          <button className="btn-icon" onClick={onClose} style={{ color: '#fff' }}><X size={20} /></button>
        </div>

        <div className="modal-body" style={{ padding: '20px 24px', background: '#fff' }}>
          {error && (
            <div className="form-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, color: '#c0392b' }}>
              <AlertCircle size={16} /><span>{error}</span>
            </div>
          )}

          {/* ========== SECTION 1: INFORMASI UMUM ========== */}
          <SectionHeader icon={<ClipboardCheck size={16} />} title="Informasi Umum" subtitle="Data kendaraan dan petugas" />

          {/* --- ROW 1: Teknisi | Kendaraan | Satgas --- */}
          <FieldGroup cols="1fr 1fr 1fr">
            <LabelInput label="Nama Teknisi *" required>
              <select value={form.teknisi} onChange={e => onTeknisiFormChange(e.target.value)}>
                <option value="">-- Pilih Teknisi --</option>
                {teknisiPenugasan.map(t => (
                  <option key={t.karyawan_id} value={t.nama}>{t.nama}</option>
                ))}
                {teknisiPenugasan.length === 0 && (
                  <option value="" disabled>Tidak ada data penugasan</option>
                )}
              </select>
              {form.teknisi && (
                <small style={{ color: 'var(--gray-400)', fontSize: '0.7rem', marginTop: 4, display: 'block' }}>
                  Pilih teknisi untuk memfilter kendaraan dari penugasan.
                </small>
              )}
            </LabelInput>
            <LabelInput label="Kendaraan (dari Penugasan)">
              <select value={form.vehicle_id} onChange={handleVehicleSelect}
                disabled={!form.teknisi}>
                <option value="">-- Pilih Kendaraan --</option>
                {form.teknisi ? filteredVehicles.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.body_no || '-'} — {v.trayek_no || v.route_code || '-'}
                  </option>
                )) : (
                  <option value="" disabled>Pilih teknisi terlebih dahulu</option>
                )}
                {form.teknisi && filteredVehicles.length === 0 && (
                  <option value="" disabled>
                    {totalAssigned === 0 ? 'Tidak ada penugasan aktif periode ini' : 'Semua kendaraan sudah selesai di-PM'}
                  </option>
                )}
              </select>
              {form.teknisi && (
                <small style={{ color: 'var(--gray-400)', fontSize: '0.7rem', marginTop: 4, display: 'block' }}>
                  {totalAssigned === 0
                    ? 'Tidak ada penugasan aktif periode ini'
                    : `Menampilkan ${filteredVehicles.length} dari ${totalAssigned} kendaraan ditugaskan`}
                </small>
              )}
            </LabelInput>
            <LabelInput label="Pengawas/Satgas">
              <input type="text"
                value={form.pengawas || ''}
                onChange={e => setForm(prev => ({ ...prev, pengawas: e.target.value }))}
                placeholder="Nama pengawas"
                autoComplete="off"
              />
            </LabelInput>
          </FieldGroup>

          {/* --- ROW 2: No Body | Nopol | AC/Non AC | Rute | Lokasi | Tanggal --- */}
          <FieldGroup cols="1fr 1fr 1fr 1fr 1fr 1fr">
            <LabelInput label="No. Body">
              <input type="text" value={form.no_body} onChange={e => setForm(prev => ({...prev, no_body: e.target.value}))} placeholder="Body" style={{ fontSize: '0.82rem' }} autoComplete="off" />
            </LabelInput>
            <LabelInput label="No. Polisi">
              <input type="text" value={form.no_polisi} onChange={e => setForm(prev => ({...prev, no_polisi: e.target.value}))} placeholder="Polisi" style={{ fontSize: '0.82rem' }} autoComplete="off" />
            </LabelInput>
            <LabelInput label="Jenis">
              {form.jenis_ac ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px',
                  border: '1px solid #b8e6d0', borderRadius: 8,
                  background: '#e8f8f0',
                  fontSize: '0.82rem', color: '#1aae6f', fontWeight: 600,
                  height: 38, boxSizing: 'border-box',
                }}>
                  <span>{form.jenis_ac}</span>
                </div>
              ) : (
                <input type="text" value={form.jenis_ac} disabled placeholder="Otomatis" style={{ fontSize: '0.82rem', color: '#999', background: '#f5f5f5' }} autoComplete="off" />
              )}
            </LabelInput>
            <LabelInput label="Rute (Trayek)">
              <input type="text" value={form.rute} onChange={e => setForm(prev => ({...prev, rute: e.target.value}))} placeholder="Rute" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2dce89' }} autoComplete="off" />
            </LabelInput>
            <LabelInput label="Lokasi">
              {form.lokasi ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px',
                  border: '1px solid #b8e6d0', borderRadius: 8,
                  background: '#e8f8f0',
                  fontSize: '0.82rem', color: '#1aae6f', fontWeight: 600,
                  height: 38, boxSizing: 'border-box',
                }}>
                  <MapPin size={14} />
                  {form.lokasi}
                </div>
              ) : (
                <input type="text" value={form.lokasi} onChange={e => setForm(prev => ({...prev, lokasi: e.target.value}))} placeholder="Lokasi" style={{ fontSize: '0.82rem' }} autoComplete="off" />
              )}
            </LabelInput>
            <LabelInput label="Tanggal/Jam *">
              <input type="datetime-local" value={form.tanggal} onChange={e => setForm(prev => ({...prev, tanggal: e.target.value}))} />
            </LabelInput>
          </FieldGroup>

          {/* ========== SECTION: DOKUMENTASI (MULTI FOTO & VIDEO) ========== */}
          <SectionHeader icon={<Camera size={16} />} title="Dokumentasi" subtitle="Upload foto dan video pemeriksaan (bisa multi-file)" />

          <FieldGroup cols="1fr 1fr">
            <MultiFileUpload
              label="Foto"
              fileType="foto"
              urls={form.foto_urls}
              uploading={uploading.foto}
              onUpload={(files) => handleMultiUpload(files, 'foto')}
              onRemove={(idx) => handleRemoveFile(idx, 'foto')}
            />
            <MultiFileUpload
              label="Video"
              fileType="video"
              urls={form.video_urls}
              uploading={uploading.video}
              onUpload={(files) => handleMultiUpload(files, 'video')}
              onRemove={(idx) => handleRemoveFile(idx, 'video')}
            />
          </FieldGroup>

          {/* ========== SECTION 2: CHECKLIST PEMERIKSAAN ========== */}
          <SectionHeader icon={<FileText size={16} />} title="Checklist Pemeriksaan" subtitle={`${form.checklist_items.filter(i => !i._hidden).length} item dalam ${[...new Set(form.checklist_items.filter(i => !i._hidden).map(i => i.kategori || 'Umum'))].length} kategori`} />

          <div style={{
            background: '#fafafa', borderRadius: 8, border: '1px solid var(--gray-100)',
            padding: 12, marginBottom: 12,
          }}>
            {(() => {
              const groups = {};
              form.checklist_items.forEach((ci, idx) => {
                if (ci._hidden) return; // skip hidden items for Non AC
                const key = ci.kategori || 'Umum';
                if (!groups[key]) groups[key] = [];
                groups[key].push({ ...ci, idx });
              });
              return Object.entries(groups).map(([kategori, items]) => (
                <div key={kategori} style={{ marginBottom: 14 }}>
                  <div style={{
                    fontSize: '0.72rem', fontWeight: 700, color: '#2dce89',
                    letterSpacing: '0.5px',
                    padding: '6px 8px', background: '#e8f8f0', borderRadius: '6px 6px 0 0',
                    borderBottom: '2px solid #2dce89', marginBottom: 6,
                  }}>
                    {kategori.replace(/\bCHECKLIST\b/gi, 'Checklist').replace(/\bOBU\b/g, 'OBU').replace(/\bRUNNING TEXT\b/gi, 'Running Text').replace(/\bMDVR & CCTV\b/gi, 'MDVR & CCTV').replace(/\bPERANGKAT TAMBAHAN\b/gi, 'Perangkat Tambahan')}
                  </div>
                  {items.map((ci) => {
                    const borderColor = ci.kondisi === 'Baik' ? '#2dce89' : ci.kondisi === 'Tidak' ? '#f5365c' : '#fb6340';
                    return (
                      <div key={ci.idx} style={{
                        display: 'grid', gridTemplateColumns: '1fr 28px 90px 1fr',
                        gap: 8, alignItems: 'center', marginBottom: 4,
                        background: '#fff', padding: '7px 10px', borderRadius: 6,
                        border: `1px solid ${borderColor}`,
                        borderLeft: `3px solid ${borderColor}`,
                        transition: 'border-color 0.2s',
                      }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--gray-700)' }}>
                          {ci.nama}
                        </span>
                        <label style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', width: 22, height: 22,
                        }} title="Centang jika sudah diperiksa">
                          <input type="checkbox"
                            checked={ci.checked || false}
                            onChange={e => updateChecklistItem(ci.idx, 'checked', e.target.checked)}
                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#2dce89' }}
                          />
                        </label>
                        <select value={ci.kondisi} onChange={e => updateChecklistItem(ci.idx, 'kondisi', e.target.value)}
                          style={{
                            padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 5,
                            fontSize: '0.78rem', background: '#fff', fontWeight: 600,
                            color: ci.kondisi === 'Baik' ? '#1aae6f' : ci.kondisi === 'Tidak' ? '#f5365c' : '#fb6340',
                          }}>
                          {KONDISI_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <input type="text" value={ci.keterangan} onChange={e => updateChecklistItem(ci.idx, 'keterangan', e.target.value)}
                          placeholder={ci.kondisi !== 'Baik' ? 'Catatan...' : '-'}
                          style={{
                            padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 5,
                            fontSize: '0.78rem',
                            fontStyle: ci.keterangan ? 'normal' : 'italic',
                            color: ci.keterangan ? 'var(--gray-700)' : 'var(--gray-400)',
                          }} />
                      </div>
                    );
                  })}
                  <div style={{
                    marginTop: 4, padding: '3px 8px',
                    fontSize: '0.7rem', color: 'var(--gray-400)',
                    display: 'flex', gap: 12,
                  }}>
                    <span style={{ color: '#1aae6f' }}>✓ Baik: {items.filter(i => i.kondisi === 'Baik').length}</span>
                    <span style={{ color: '#f5365c' }}>✗ Tidak: {items.filter(i => i.kondisi === 'Tidak').length}</span>
                  </div>
                </div>
              ));
            })()}

            <div style={{
              marginTop: 4, padding: '10px 14px', background: '#fff', borderRadius: 8,
              border: '1px solid var(--gray-200)', display: 'flex', gap: 16, flexWrap: 'wrap',
              fontSize: '0.82rem',
            }}>
              <span style={{ color: '#1aae6f', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle size={14} /> Baik: {form.checklist_items.filter(i => !i._hidden && i.kondisi === 'Baik').length}
              </span>
              <span style={{ color: '#f5365c', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <XCircle size={14} /> Tidak: {form.checklist_items.filter(i => !i._hidden && i.kondisi === 'Tidak').length}
              </span>
            </div>
          </div>

          {/* ========== SECTION 3: CATATAN ========== */}
          <SectionHeader icon={<FileText size={16} />} title="Catatan" subtitle="Informasi tambahan" />

          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Catatan Pemeriksaan</label>
            <textarea
              rows={3}
              value={form.catatan}
              onChange={e => setForm(prev => ({...prev, catatan: e.target.value}))}
              placeholder="Catatan hasil pemeriksaan, rekomendasi, atau tindak lanjut yang diperlukan ..."
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid var(--gray-200)',
                borderRadius: 8, fontSize: '0.85rem', resize: 'vertical',
                fontFamily: 'inherit', lineHeight: 1.6,
              }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{
          padding: '12px 24px', borderTop: '1px solid var(--gray-100)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
          background: '#fafafa', borderRadius: '0 0 12px 12px',
          position: 'sticky', bottom: 0, zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(() => {
              const visibleItems = form.checklist_items.filter(i => !i._hidden);
              const total = visibleItems.length;
              const checked = visibleItems.filter(i => i.checked).length;
              const allChecked = checked === total;
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 14px', borderRadius: 8,
                  background: allChecked ? '#e8f8f0' : '#fff8e1',
                  border: allChecked ? '1px solid #b8e6d0' : '1px solid #ffeeba',
                }}>
                  {allChecked ? (
                    <>
                      <CheckCircle size={16} color="#1aae6f" />
                      <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1aae6f' }}>Selesai</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={16} color="#fb6340" />
                      <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#fb6340' }}>
                        {checked}/{total} centang
                      </span>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={onClose}>Batal</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || uploading.foto || uploading.video}
              style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', border: 'none' }}>
              {saving ? 'Menyimpan...' : editing ? 'Perbarui' : 'Simpan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ====== Main PM Component ======
export default function PM() {
  const { hasMenuAction, user } = useAuth();
  const [data, setData] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [penugasanData, setPenugasanData] = useState([]);
  const [cmData, setCmData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterNoBody, setFilterNoBody] = useState('');
  const [filterNoPolisi, setFilterNoPolisi] = useState('');
  const [filterTeknisi, setFilterTeknisi] = useState('');
  const [filterTglAwal, setFilterTglAwal] = useState('');
  const [filterTglAkhir, setFilterTglAkhir] = useState('');
  const [filterLokasi, setFilterLokasi] = useState('');
  const [filterNamaKlien, setFilterNamaKlien] = useState('');
  const [filterJenisKendaraan, setFilterJenisKendaraan] = useState('');
  const [exportTglAwal, setExportTglAwal] = useState('');
  const [exportTglAkhir, setExportTglAkhir] = useState('');
  const [showExportFilter, setShowExportFilter] = useState(false);
  const [modalState, setModalState] = useState(null); // null, {mode:'create'}, or {mode:'edit', item}
  const [selectedTeknisi, setSelectedTeknisi] = useState('');

  // Data PM lengkap untuk keperluan export. /api/pm dibatasi 500 baris terbaru,
  // sehingga export periode lama (mis. bulan lalu) bisa terpotong. /api/pm/export
  // mengembalikan seluruh baris tanpa kolom berat (checklist/ttd/foto) → ringan.
  const fullPMRef = useRef(null);
  const fetchFullPM = useCallback(async () => {
    if (fullPMRef.current) return fullPMRef.current;
    const res = await getPMExport();
    const rows = Array.isArray(res.data) ? res.data : [];
    fullPMRef.current = rows;
    return rows;
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    fullPMRef.current = null; // data PM berubah → cache export di-reset
    Promise.all([
      getPM().catch(err => { console.error('getPM error:', err); return { data: [] }; }),
      getVehicles({ all: true }).catch(err => { console.error('getVehicles error:', err); return { data: { data: [] } }; }),
      getPenugasan().catch(err => { console.error('getPenugasan error:', err); return { data: [] }; }),
      getCM().catch(err => { console.error('getCM error:', err); return { data: [] }; }),
    ]).then(([res, vehRes, penRes, cmRes]) => {
      setData(res.data);
      setVehicles(vehRes.data?.data || []);
      setPenugasanData(penRes.data || []);
      setCmData(Array.isArray(cmRes.data) ? cmRes.data : []);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Derive unik teknisi from penugasan data
  const teknisiPenugasan = [...new Map(
    penugasanData
      .filter(p => p.karyawan_nama && p.vehicle_data?.length > 0)
      .map(p => [p.karyawan_id, { karyawan_id: p.karyawan_id, nama: p.karyawan_nama }])
  ).values()].filter(t => t.karyawan_id);

  // Periode penugasan aktif untuk teknisi yang dipilih (data PM mengacu pada penugasan periode berjalan)
  const activePeriodsForSelected = selectedTeknisi
    ? getActivePenugasanPeriods(penugasanData, selectedTeknisi)
    : [];

  // Get vehicle IDs that already have PM (status Selesai) IN this period for this teknisi
  const completedVehicleIds = new Set(
    data
      .filter(pm => {
        if (!selectedTeknisi) return false;
        return pm.teknisi?.toLowerCase() === selectedTeknisi.toLowerCase()
          && pm.status === 'Selesai'
          && pmFallsInPeriod(String(pm.tanggal || '').slice(0, 10), activePeriodsForSelected);
      })
      .map(pm => pm.vehicle_id)
      .filter(Boolean)
  );

  const penugasanForTeknisi = activePeriodsForSelected;

  const allVehicleData = penugasanForTeknisi.flatMap(p => p.vehicle_data || []);
  const assignedVehicleIds = new Set(allVehicleData.map(v => v.id));


  const totalAssigned = assignedVehicleIds.size;
  const completedCount = completedVehicleIds.size;
  const remainingCount = totalAssigned - completedCount;
  const progressPct = totalAssigned > 0 ? Math.round((completedCount / totalAssigned) * 100) : 0;

  // Map PM ID → CM status display: 'Selesai' | 'Diproses' | null
  const pmCmStatusMap = {};
  cmData.forEach(c => {
    if (c.pm_id) {
      const existing = pmCmStatusMap[c.pm_id];
      // Prioritaskan 'Selesai' jika ada multiple CM untuk PM yang sama
      if (!existing || c.status === 'Selesai') {
        pmCmStatusMap[c.pm_id] = c.status === 'Selesai' ? 'Selesai' : 'Diproses';
      }
    }
  });

  const filtered = data.filter(d => {
    if (filterNoBody && !d.no_body?.toLowerCase().includes(filterNoBody.toLowerCase())) return false;
    if (filterNoPolisi && !d.no_polisi?.toLowerCase().includes(filterNoPolisi.toLowerCase())) return false;
    if (filterTeknisi && !d.teknisi?.toLowerCase().includes(filterTeknisi.toLowerCase())) return false;
    if (filterTglAwal && d.tanggal && d.tanggal.slice(0, 10) < filterTglAwal) return false;
    if (filterLokasi && !d.lokasi?.toLowerCase().includes(filterLokasi.toLowerCase())) return false;
    if (filterNamaKlien && !(d.nama_klien || '').toLowerCase().includes(filterNamaKlien.toLowerCase())) return false;
    if (filterJenisKendaraan) {
      const jenisAc = d.jenis_ac || '';
      if (jenisAc !== filterJenisKendaraan) return false;
    }
    if (filterTglAkhir && d.tanggal && d.tanggal.slice(0, 10) > filterTglAkhir) return false;
    return true;
  });

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus PM ini?`)) return;
    try { await deletePM(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  // ===== Build per-teknisi report data =====
  const buildTeknisiReport = useCallback((dateFilter, mainFilters, pmSource) => {
    const { startDate, endDate } = dateFilter || {};
    const mf = mainFilters || {};
    // Sumber data PM: pakai data penuh hasil /api/pm/export bila tersedia
    const pmData = Array.isArray(pmSource) ? pmSource : data;
    // Helper to check if a PM date falls within the range
    const inDateRange = (pm) => {
      if (!startDate && !endDate) return true;
      const pmDate = pm.tanggal?.slice(0, 10);
      if (!pmDate) return !startDate && !endDate;
      if (startDate && pmDate < startDate) return false;
      if (endDate && pmDate > endDate) return false;
      return true;
    };

    // Helper to check if vehicle passes main filters
    const passMainFilters = (v) => {
      if (mf.filterNoBody && !(v.noBody || '').toLowerCase().includes(mf.filterNoBody.toLowerCase())) return false;
      if (mf.filterNoPolisi && !(v.noPolisi || '').toLowerCase().includes(mf.filterNoPolisi.toLowerCase())) return false;
      if (mf.filterLokasi && !(v._lokasi || '').toLowerCase().includes(mf.filterLokasi.toLowerCase())) return false;
      if (mf.filterNamaKlien && !(v._namaKlien || '').toLowerCase().includes(mf.filterNamaKlien.toLowerCase())) return false;
      if (mf.filterJenisKendaraan && (v._jenisAC || '') !== mf.filterJenisKendaraan) return false;
      return true;
    };

    // For each teknisi in penugasan, get vehicles and PM status
    const report = teknisiPenugasan.map(t => {
      const nama = t.nama;
      // Skip teknisi if filterTeknisi is set and doesn't match
      if (mf.filterTeknisi && !nama.toLowerCase().includes(mf.filterTeknisi.toLowerCase())) return null;
      // Data PM mengacu pada penugasan periode berjalan (penugasan aktif).
      // Jika export memakai filter tanggal historis, sertakan juga penugasan yang
      // periodenya beririsan dengan rentang export agar laporan bulan lalu tetap muncul.
      let penugasanForT = getActivePenugasanPeriods(penugasanData, nama);
      if (startDate || endDate) {
        const overlapping = penugasanData.filter(p => {
          if (p.karyawan_nama?.toLowerCase() !== nama.toLowerCase()) return false;
          if (penugasanForT.includes(p)) return false;
          const ps = String(p.tanggal_mulai || '').slice(0, 10);
          const pe = String(p.tanggal_selesai || '').slice(0, 10);
          if (startDate && pe && pe < startDate) return false;
          if (endDate && ps && ps > endDate) return false;
          return true;
        });
        penugasanForT = [...penugasanForT, ...overlapping];
      }
      const vehiclesForT = penugasanForT.flatMap(p => p.vehicle_data || []);
      // Get completed PM for this teknisi IN the active period (filtered by date range if provided)
      const completedPMs = pmData.filter(pm =>
        pm.teknisi?.toLowerCase() === nama.toLowerCase() && pm.status === 'Selesai'
        && pmFallsInPeriod(String(pm.tanggal || '').slice(0, 10), penugasanForT)
        && inDateRange(pm)
      );
      const completedVehicleIds = new Set(completedPMs.map(pm => pm.vehicle_id).filter(Boolean));

      // Map each vehicle with status, filtered by mainFilters
      const vehicleDetails = vehiclesForT
        .map(v => {
          // Get full vehicle data for filtering (always available)
          const fv = vehicles.find(fullV => fullV.id === v.id) || {};
          return {
            id: v.id,
            noBody: v.no_body || v.body_no || '-',
            noPolisi: v.no_polisi || v.plate_no || '-',
            brand: v.brand || '-',
            trayek: v.trayek_no || '-',
            statusPM: completedVehicleIds.has(v.id) ? 'Sudah PM' : 'Belum PM',
            // Internal fields for filtering (from full vehicle data)
            _lokasi: fv.pengendapan || '',
            _namaKlien: fv.operator || '',
            _jenisAC: fv.jenis_ac || '',
          };
        })
        .filter(v => passMainFilters(v))
        .map(({ _lokasi, _namaKlien, _jenisAC, ...rest }) => rest); // remove internal fields

      const total = vehicleDetails.length;
      // Recalculate completed based on filtered vehicles
      const completedFiltered = vehicleDetails.filter(v => v.statusPM === 'Sudah PM').length;
      const remaining = total - completedFiltered;
      const persen = total > 0 ? Math.round((completedFiltered / total) * 100) : 0;

      return {
        nama,
        total,
        completed: completedFiltered,
        remaining,
        persen,
        vehicles: vehicleDetails,
      };
    }).filter(r => r !== null && r.total > 0); // Only include teknisi with actual assignments

    return report;
  }, [teknisiPenugasan, penugasanData, data, vehicles]);

  // ===== Export to CSV (Excel) — Flat 11 kolom =====
  const exportCSV = async () => {
    // Sumber data = data PM penuh dari /api/pm/export agar periode lama tidak
    // terpotong limit 500 baris /api/pm. Fallback ke data yang sudah dimuat.
    let source = data;
    try {
      source = await fetchFullPM();
    } catch (err) {
      console.warn('getPMExport gagal, memakai data PM yang sudah dimuat:', err);
    }
    // Filter data PM dengan filter yang sama seperti tabel visible
    const filteredData = source.filter(d => {
      if (filterNoBody && !d.no_body?.toLowerCase().includes(filterNoBody.toLowerCase())) return false;
      if (filterNoPolisi && !d.no_polisi?.toLowerCase().includes(filterNoPolisi.toLowerCase())) return false;
      if (filterTeknisi && !d.teknisi?.toLowerCase().includes(filterTeknisi.toLowerCase())) return false;
      if (filterTglAwal && d.tanggal && d.tanggal.slice(0, 10) < filterTglAwal) return false;
      if (filterLokasi && !d.lokasi?.toLowerCase().includes(filterLokasi.toLowerCase())) return false;
      if (filterNamaKlien && !(d.nama_klien || '').toLowerCase().includes(filterNamaKlien.toLowerCase())) return false;
      if (filterJenisKendaraan) {
        if ((d.jenis_ac || '') !== filterJenisKendaraan) return false;
      }
      if (filterTglAkhir && d.tanggal && d.tanggal.slice(0, 10) > filterTglAkhir) return false;
      // Tambahan filter exportTglAwal/Akhir
      if (exportTglAwal && d.tanggal && d.tanggal.slice(0, 10) < exportTglAwal) return false;
      if (exportTglAkhir && d.tanggal && d.tanggal.slice(0, 10) > exportTglAkhir) return false;
      return true;
    });

    if (filteredData.length === 0) {
      alert('Tidak ada data PM untuk di-export');
      return;
    }

    const escapeCSV = (s) => {
      if (s == null) return '';
      const str = String(s).replace(/[\r\n]+/g, ' ').trim();
      if (str.includes(',') || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvRows = [];
    // Header 11 kolom
    csvRows.push([
      'CM_PM', 'Nama Klien', 'Tanggal PM', 'No Body', 'No Polisi',
      'Rute', 'Jenis AC/non AC', 'Aktifitas PM',
      'Nama Teknisi', 'Status', 'Keterangan'
    ].join(','));

    filteredData.forEach(d => {
      const tglPM = d.tanggal ? new Date(d.tanggal).toLocaleDateString('id-ID', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Asia/Jakarta' }) : '';
      csvRows.push([
        escapeCSV(d.pm_id || `#${d.id}`),
        escapeCSV(d.nama_klien || ''),
        escapeCSV(tglPM),
        escapeCSV(d.no_body || ''),
        escapeCSV(d.no_polisi || ''),
        escapeCSV(d.rute || ''),
        escapeCSV(d.jenis_ac || ''),
        'Pemeriksaan Rutin',
        escapeCSV(d.teknisi || ''),
        escapeCSV(d.status || ''),
        escapeCSV(d.catatan || ''),
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const tanggal = new Date().toISOString().slice(0, 10);
    const hasFilter = exportTglAwal || exportTglAkhir || filterNoBody || filterNoPolisi || filterTeknisi || filterLokasi || filterNamaKlien || filterJenisKendaraan;
    const periodFile = hasFilter ? `_${exportTglAwal || 'all'}_${exportTglAkhir || 'all'}` : '';
    link.download = `Laporan_PM${periodFile}_${tanggal}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // ===== Export to PDF =====
  const exportPDF = async () => {
    const dateFilter = {};
    if (exportTglAwal) dateFilter.startDate = exportTglAwal;
    if (exportTglAkhir) dateFilter.endDate = exportTglAkhir;
    const hasFilter = exportTglAwal || exportTglAkhir || filterNoBody || filterNoPolisi || filterTeknisi || filterLokasi || filterNamaKlien || filterJenisKendaraan;

    const mainFilters = {
      filterNoBody, filterNoPolisi, filterTeknisi, filterLokasi,
      filterNamaKlien, filterJenisKendaraan,
    };
    // Pakai data PM penuh untuk status "Sudah PM" agar periode historis akurat
    let pmSource = data;
    try {
      pmSource = await fetchFullPM();
    } catch (err) {
      console.warn('getPMExport gagal, memakai data PM yang sudah dimuat:', err);
    }
    const report = buildTeknisiReport(dateFilter, mainFilters, pmSource);
    if (report.length === 0) {
      alert('Tidak ada data penugasan untuk di-export');
      return;
    }

    const fmtPeriod = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
    const periodLabel = hasFilter
      ? `Periode: ${fmtPeriod(exportTglAwal)} — ${fmtPeriod(exportTglAkhir)}`
      : 'Periode: Semua Data';

    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');

    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 12;
    const primary = [45, 206, 137]; // #2dce89 green
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];

    // Cover header
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('LAPORAN PM PER TEKNISI', margin, 16);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const dateStr = new Date().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(`Dicetak: ${dateStr} | LayananSynexa FMS`, margin, 25);
    doc.setFontSize(8);
    doc.text(periodLabel, margin, 30);
    doc.setFont('helvetica', 'bold');
    doc.text('PT. Synexa Global Tekno', pageW - margin, 16, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Sistem Informasi Armada', pageW - margin, 23, { align: 'right' });

    const startY = 44;

    // Grand total summary
    const grandTotal = report.reduce((s, r) => s + r.total, 0);
    const grandCompleted = report.reduce((s, r) => s + r.completed, 0);
    const grandRemaining = report.reduce((s, r) => s + r.remaining, 0);
    const grandPersen = grandTotal > 0 ? Math.round((grandCompleted / grandTotal) * 100) : 0;

    // Summary table per teknisi
    const summaryHeaders = [['No', 'Teknisi', 'Total Kendaraan', 'Sudah PM', 'Belum PM', 'Progres']];
    const summaryRows = report.map((r, idx) => [
      String(idx + 1),
      r.nama,
      String(r.total),
      String(r.completed),
      String(r.remaining),
      `${r.persen}%`,
    ]);
    summaryRows.push([
      '',
      { content: 'GRAND TOTAL', styles: { fontStyle: 'bold', fillColor: [248, 249, 252] } },
      { content: String(grandTotal), styles: { fontStyle: 'bold', fillColor: [248, 249, 252] } },
      { content: String(grandCompleted), styles: { fontStyle: 'bold', fillColor: [248, 249, 252] } },
      { content: String(grandRemaining), styles: { fontStyle: 'bold', fillColor: [248, 249, 252] } },
      { content: `${grandPersen}%`, styles: { fontStyle: 'bold', fillColor: [248, 249, 252] } },
    ]);

    autoTable(doc, {
      startY,
      margin: { left: margin, right: margin },
      head: summaryHeaders,
      body: summaryRows,
      styles: { fontSize: 8, cellPadding: 3, lineColor: [210, 210, 210], lineWidth: 0.4, textColor: [50, 50, 50] },
      headStyles: { fillColor: [45, 206, 137], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8, halign: 'center' },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 60 },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 30, halign: 'center' },
        4: { cellWidth: 30, halign: 'center' },
        5: { cellWidth: 25, halign: 'center' },
      },
    });

    // Detail per teknisi
    let detailY = doc.lastAutoTable.finalY + 10;

    report.forEach((r, idx) => {
      // Check if we need a new page
      const rowCount = r.vehicles.length + 2;
      const neededHeight = rowCount * 8 + 20;
      const pageHeight = doc.internal.pageSize.getHeight();
      if (detailY + neededHeight > pageHeight - 20) {
        doc.addPage();
        detailY = 15;
      }

      // Section header for this teknisi
      doc.setFillColor(240, 248, 244);
      doc.rect(margin, detailY, pageW - 2 * margin, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...dark);
      doc.text(`${idx + 1}. ${r.nama} — ${r.completed}/${r.total} selesai (${r.persen}%)`, margin + 2, detailY + 6);

      detailY += 10;

      const detailHeaders = [['No', 'No Body', 'No Polisi', 'Brand', 'Trayek', 'Status PM']];
      const detailRows = r.vehicles.map((v, vi) => [
        String(vi + 1),
        v.noBody,
        v.noPolisi,
        v.brand,
        v.trayek,
        { content: v.statusPM, styles: { fontStyle: 'bold', textColor: v.statusPM === 'Sudah PM' ? [26, 174, 111] : [245, 54, 92] } },
      ]);

      autoTable(doc, {
        startY: detailY,
        margin: { left: margin + 5, right: margin },
        head: detailHeaders,
        body: detailRows,
        styles: { fontSize: 7, cellPadding: 2, lineColor: [220, 220, 220], lineWidth: 0.3, textColor: [50, 50, 50] },
        headStyles: { fillColor: [45, 206, 137], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
          3: { cellWidth: 40 },
          4: { cellWidth: 30 },
          5: { cellWidth: 30, halign: 'center' },
        },
      });

      detailY = doc.lastAutoTable.finalY + 6;
    });

    // Footer on each page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const h = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, h - 14, pageW - margin, h - 14);
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak: ${dateStr} | LayananSynexa FMS`, margin, h - 7);
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, h - 7, { align: 'right' });
    }

    const tanggal = new Date().toISOString().slice(0, 10);
    const periodFile = hasFilter ? `_${exportTglAwal}_${exportTglAkhir}` : '';
    doc.save(`Laporan_PM_Per_Teknisi${periodFile}_${tanggal}.pdf`);
  };

  const handleCetak = (item) => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) { alert('Popup diblokir. Izinkan popup untuk mencetak.'); return; }

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
    const sekarang = new Date();

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Cetak PM - ${item.no_polisi || ''}</title>
<style>
  @page { margin: 1.5cm 1cm; }
  body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; color: #000; line-height: 1.4; }
  
  .no-print { text-align: center; margin-bottom: 16px; }
  .no-print button { padding: 8px 20px; background: #2dce89; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; font-family: sans-serif; }
  
  .header { text-align: center; margin-bottom: 16px; }
  .header .tagline { font-size: 9pt; font-style: italic; color: #555; margin-bottom: 2px; }
  .header .brand { font-size: 18pt; font-weight: 700; letter-spacing: 3px; margin: 4px 0 2px; }
  .header .company { font-size: 11pt; font-weight: 700; }
  .header .contact { font-size: 9pt; color: #555; margin-bottom: 4px; }
  .header .contact a { color: #000; text-decoration: none; }
  .header .title { font-size: 14pt; font-weight: 700; margin: 4px 0 2px; text-transform: uppercase; }
  
  .section-title { font-weight: 700; font-size: 10pt; margin: 14px 0 6px; text-transform: uppercase; }
  
  table.identity { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 10pt; }
  table.identity td { padding: 2px 4px; vertical-align: top; }
  table.identity td.label { font-weight: 700; width: 130px; }
  table.identity td.colon { width: 12px; text-align: center; }
  table.identity td.value { border-bottom: 1px solid #000; padding: 0 4px; }
  
  table.checklist { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 9pt; }
  table.checklist th { border: 1px solid #000; padding: 4px 5px; text-align: center; font-weight: 700; background: #f0f0f0; font-size: 9pt; }
  table.checklist th.baik { color: #1aae6f; }
  table.checklist th.tidak { color: #f5365c; }
  table.checklist td { border: 1px solid #000; padding: 3px 5px; vertical-align: middle; }
  table.checklist td.no { width: 28px; text-align: center; }
  table.checklist td.baik-col { width: 28px; text-align: center; font-size: 11pt; }
  table.checklist td.tidak-col { width: 28px; text-align: center; font-size: 11pt; }
  table.checklist td.ket { width: 100px; }
  
.dok-gallery { display: flex; flex-wrap: wrap; gap: 10px; margin: 10px 0; }
.dok-section { flex: 1 1 30%; min-width: 180px; max-width: 280px; }
.dok-section img { width: 100%; height: 160px; object-fit: cover; border: 1px solid #ccc; border-radius: 4px; }
  
  .catatan-text { margin: 4px 0 10px; text-align: justify; font-size: 10pt; line-height: 1.5; }
  
  .ttd { display: flex; justify-content: space-between; margin-top: 30px; padding: 0 10px; }
  .ttd-box { text-align: center; width: 160px; }
  .ttd-box .ttd-label { font-size: 9pt; margin-bottom: 2px; }
  .ttd-box .ttd-name { font-size: 10pt; font-weight: 600; border-top: 1px solid #000; padding-top: 3px; }
  
  .footer { text-align: center; font-size: 8pt; color: #888; margin-top: 20px; border-top: 1px solid #ccc; padding-top: 4px; }
  
  @media print { .no-print { display: none !important; } }
</style></head><body>

<div class="no-print"><button onclick="window.print()">🖨 Cetak Halaman</button></div>

<div class="header">
  <div class="tagline">e integrated Smart Technology</div>
  <div class="brand">SYNEXA</div>
  <div class="company">PT. Synexa Global Tekno</div>
  <div class="contact">Website: <a href="http://www.synexa.co.id">www.synexa.co.id</a> &mdash; E-mail: <a href="mailto:info@synexa.co.id">info@synexa.co.id</a></div>
  <div class="title">FORMULIR PEMELIHARAAN BULANAN ARMADA</div>
</div>

<table class="identity">
  <tr><td class="label" style="width:100px">Nama Klien</td><td class="colon">:</td><td class="value">${item.nama_klien || '______________________'}</td>
      <td class="label" style="width:115px">Hari/Tanggal</td><td class="colon">:</td><td class="value">${item.tanggal ? fmtDate(item.tanggal) : '__________________'}</td></tr>
</table>

<div class="section-title">IDENTITAS ARMADA</div>
<table class="identity">
  <tr><td class="label">No. Polisi</td><td class="colon">:</td><td class="value" style="font-weight:700">${item.no_polisi || '______________'}</td>
      <td class="label" style="width:120px">No. Body</td><td class="colon">:</td><td class="value">${item.no_body || '______________'}</td></tr>
  <tr><td class="label">Odometer</td><td class="colon">:</td><td class="value">${item.odometer ? item.odometer + ' km' : '______________ km'}</td>
      <td class="label">Status</td><td class="colon">:</td><td class="value">${item.status || '______________'}</td></tr>
  <tr><td class="label">Teknisi</td><td class="colon">:</td><td class="value">${item.teknisi || '______________'}</td>
      <td class="label">Pengawas</td><td class="colon">:</td><td class="value">${item.pengawas || '______________'}</td></tr>
</table>

<div class="section-title">HASIL PEMERIKSAAN</div>

<table class="checklist">
  <thead>
    <tr><th class="no">No</th><th class="item">Komponen Yang Diperiksa</th><th class="baik">Baik</th><th class="tidak">Tidak</th><th class="ket">Keterangan</th></tr>
  </thead>
  <tbody>`);

    const kategoriNumbers = {
      'CHECKLIST OBU': '1.',
      'CHECKLIST RUNNING TEXT': '2.',
      'CHECKLIST MDVR & CCTV': '3.',
      'CHECKLIST PERANGKAT TAMBAHAN': '4.',
    };
    const cetakGroups = {};
    (item.checklist_items || []).forEach(ci => {
      const key = ci.kategori || 'PEMERIKSAAN';
      if (!cetakGroups[key]) cetakGroups[key] = [];
      cetakGroups[key].push(ci);
    });
    let noUrut = 0;
    Object.keys(cetakGroups).forEach(kategori => {
      const items = cetakGroups[kategori];
      // Category header row (numbered)
      const catLabel = (kategoriNumbers[kategori] || '') + ' ' + kategori;
      printWindow.document.write(`<tr style="background:#e8f8f0"><td colspan="5" style="padding:5px 8px;font-weight:700;font-size:9pt;border:1px solid #000;color:#1aae6f">${catLabel}</td></tr>`);
      items.forEach(ci => {
        noUrut++;
        const bgColor = (noUrut % 2 === 0) ? '#fff' : '#f9f9f9';
        const isBaik = ci.kondisi === 'Baik';
        const baikIcon = isBaik ? '◉' : '○';
        const tidakIcon = isBaik ? '○' : '◉';
        printWindow.document.write(`<tr style="background:${bgColor}">
  <td class="no">${noUrut}</td>
  <td class="item">${ci.nama}</td>
  <td class="baik-col" style="color:${isBaik ? '#1aae6f' : '#ccc'};font-weight:${isBaik ? '700' : '400'}">${baikIcon}</td>
  <td class="tidak-col" style="color:${isBaik ? '#ccc' : '#f5365c'};font-weight:${isBaik ? '400' : '700'}">${tidakIcon}</td>
  <td class="ket">${ci.keterangan || ''}</td>
</tr>`);
      });
    });

    printWindow.document.write(`</tbody></table>`);

    // Dokumentasi (support multi foto & video)
    const fotoUrls = item.foto_urls || (item.foto_url ? [item.foto_url] : []);
    const videoUrls = item.video_urls || (item.video_url ? [item.video_url] : []);
    if (fotoUrls.length > 0 || videoUrls.length > 0) {
      printWindow.document.write(`<div class="section-title">DOKUMENTASI PEKERJAAN</div>`);
      // Render multiple photos in a flex gallery
      if (fotoUrls.length > 0) {
        printWindow.document.write('<div class="dok-gallery">');
        fotoUrls.forEach((url, idx) => {
          printWindow.document.write(`<div class="dok-section"><img src="${url}" alt="Foto ${idx+1}" /></div>`);
        });
        printWindow.document.write('</div>');
      }
      // Render multiple videos
      videoUrls.forEach((url) => {
        printWindow.document.write(`<div class="dok-section"><a href="${url}" target="_blank">Lihat Video Dokumentasi</a></div>`);
      });
    }

    // Catatan
    if (item.catatan) {
      printWindow.document.write(`<div class="section-title">CATATAN</div>
<div class="catatan-text">${item.catatan.replace(/\n/g, '<br/>')}</div>`);
    }

    // Signature — render TTD image (if exists) + name below
    const renderTtdImg = (img) => {
      if (img && typeof img === 'string' && (img.startsWith('data:') || img.startsWith('http'))) {
        return `<img src="${img}" style="max-height:55px;max-width:160px;display:block;margin:0 auto 4px;" alt="TTD" onerror="this.style.display='none'" />`;
      }
      return '';
    };
    printWindow.document.write(`<div class="ttd">
<div class="ttd-box"><div class="ttd-label">Pramudi/Satgas</div>${renderTtdImg(item.ttd_pengawas)}<div class="ttd-name">${item.pengawas || '______________'}</div></div>
<div class="ttd-box"><div class="ttd-label">Teknisi</div>${renderTtdImg(item.ttd_teknisi)}<div class="ttd-name">${item.teknisi || '______________'}</div></div>
<div class="ttd-box"><div class="ttd-label">SPV Teknisi</div>${renderTtdImg(item.ttd_spv)}<div class="ttd-name">${item.pengawas || '______________'}</div></div>
</div>
<div class="footer">Dicetak dari sistem LayananSynexa FMS &mdash; ${fmtDate(sekarang)} &mdash; Dokumen resmi, tidak memerlukan tanda tangan basah</div>
</body></html>`);

    printWindow.document.close();
    setTimeout(() => { try { printWindow.print(); } catch(e) {} }, 500);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Preventive Maintenance (PM)</h2>
            <p className="page-desc">{data.length} jadwal pemeliharaan</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-secondary" onClick={exportCSV}
              style={{ whiteSpace: 'nowrap' }}>
              <Download size={15} /><span>Excel</span>
            </button>
            <button className="btn btn-secondary" onClick={exportPDF}
              style={{ whiteSpace: 'nowrap' }}>
              <FileText size={15} /><span>PDF</span>
            </button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-secondary" onClick={() => setShowExportFilter(!showExportFilter)}
                style={{
                  whiteSpace: 'nowrap',
                  background: showExportFilter ? '#e8f8f0' : undefined,
                  borderColor: showExportFilter ? '#2dce89' : undefined,
                }}>
                <Calendar size={15} />
                <span>
                  {exportTglAwal || exportTglAkhir
                    ? `${exportTglAwal || '∞'} — ${exportTglAkhir || '∞'}`
                    : 'Filter'}
                </span>
                {(exportTglAwal || exportTglAkhir) && (
                  <span style={{
                    marginLeft: 4, background: '#2dce89', color: '#fff',
                    borderRadius: 10, padding: '0 6px', fontSize: '0.65rem', fontWeight: 700,
                  }}>1</span>
                )}
              </button>
              {showExportFilter && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 6,
                  background: '#fff', borderRadius: 10, padding: 12,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)', border: '1px solid var(--gray-100)',
                  zIndex: 100, minWidth: 260,
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>
                    📅 Filter Periode Export
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: '0.65rem', color: 'var(--gray-400)', display: 'block', marginBottom: 2 }}>Dari</label>
                      <input type="date" value={exportTglAwal}
                        onChange={e => setExportTglAwal(e.target.value)}
                        style={{
                          padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 6,
                          fontSize: '0.82rem', background: '#fff',
                        }} />
                    </div>
                    <span style={{ marginTop: 16, color: 'var(--gray-400)' }}>—</span>
                    <div>
                      <label style={{ fontSize: '0.65rem', color: 'var(--gray-400)', display: 'block', marginBottom: 2 }}>Sampai</label>
                      <input type="date" value={exportTglAkhir}
                        onChange={e => setExportTglAkhir(e.target.value)}
                        style={{
                          padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 6,
                          fontSize: '0.82rem', background: '#fff',
                        }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
                    {(exportTglAwal || exportTglAkhir) && (
                      <button className="btn btn-secondary"
                        onClick={() => { setExportTglAwal(''); setExportTglAkhir(''); }}
                        style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
                        Reset
                      </button>
                    )}
                    <button className="btn btn-primary"
                      onClick={() => setShowExportFilter(false)}
                      style={{
                        fontSize: '0.75rem', padding: '4px 12px',
                        background: 'linear-gradient(135deg, #2dce89, #1aae6f)', border: 'none',
                      }}>
                      Tutup
                    </button>
                  </div>
                </div>
              )}
            </div>
            {hasMenuAction('pm', 'create') && (
              <button className="btn btn-primary" onClick={() => setModalState({ mode: 'create' })}
                style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', border: 'none' }}>
                <Plus size={16} /><span>Buat PM Baru</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Step 1: Pilih Teknisi dari Penugasan */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: selectedTeknisi ? 'linear-gradient(135deg, #e8f8f0, #f0fdf4)' : 'linear-gradient(135deg, #fff8e1, #fff3cd)',
        border: selectedTeknisi ? '1px solid #b8e6d0' : '1px solid #ffeeba',
        borderRadius: 12, padding: '14px 20px', marginBottom: 16,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: selectedTeknisi ? 'linear-gradient(135deg, #2dce89, #1aae6f)' : 'linear-gradient(135deg, #fb6340, #f5365c)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', flexShrink: 0,
        }}>
          {selectedTeknisi ? <CheckCircle size={20} /> : <User size={20} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>
            LANGKAH 1 {selectedTeknisi ? '✓ Selesai' : ''}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)', fontWeight: 500 }}>Pilih Teknisi:</span>
            <select value={selectedTeknisi} onChange={e => setSelectedTeknisi(e.target.value)}
              style={{
                padding: '8px 14px', border: '2px solid #2dce89', borderRadius: 8,
                fontSize: '0.9rem', fontWeight: 600, background: '#fff', minWidth: 220,
                cursor: 'pointer',
              }}>
              <option value="">-- Pilih Teknisi dari Penugasan --</option>
              {teknisiPenugasan.map(t => {
                const pCount = penugasanData
                  .filter(p => p.karyawan_id === t.karyawan_id)
                  .reduce((sum, p) => sum + (p.vehicle_data?.length || 0), 0);
                return (
                  <option key={t.karyawan_id} value={t.nama}>
                    {t.nama} — {pCount} unit kendaraan
                  </option>
                );
              })}
              {teknisiPenugasan.length === 0 && (
                <option value="" disabled>Belum ada penugasan dengan kendaraan</option>
              )}
            </select>
          </div>
        </div>
        {selectedTeknisi && (
          <button className="btn-icon" title="Ganti Teknisi" onClick={() => setSelectedTeknisi('')}
            style={{ color: '#f5365c', flexShrink: 0 }}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* Performance Summary Card */}
      {selectedTeknisi && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          background: 'linear-gradient(135deg, #e8f8f0, #f0fdf4)',
          border: '1px solid #b8e6d0', borderRadius: 12,
          padding: '14px 20px', marginBottom: 16,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #2dce89, #1aae6f)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', flexShrink: 0,
          }}>
            <BarChart3 size={22} />
          </div>
          <div style={{ flex: 1, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 600 }}>Teknisi</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1aae6f' }}>{selectedTeknisi}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 600 }}>Ditugaskan</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{totalAssigned} unit</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 600 }}>Selesai</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#2dce89' }}>{completedCount} unit</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 600 }}>Sisa</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fb6340' }}>{remainingCount} unit</div>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Progres</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  flex: 1, height: 8, borderRadius: 4,
                  background: '#d4edda', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${progressPct}%`, height: '100%',
                    background: 'linear-gradient(90deg, #2dce89, #1aae6f)',
                    borderRadius: 4, transition: 'width 0.5s ease',
                  }} />
                </div>
                <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1aae6f', minWidth: 44, textAlign: 'right' }}>{progressPct}%</span>
              </div>
            </div>
          </div>
          <button className="btn-icon" title="Reset" onClick={() => setSelectedTeknisi('')}
            style={{ color: '#f5365c', flexShrink: 0 }}>
            <X size={16} />
          </button>
        </div>
      )}

      <div className="filters" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="search-box" style={{ minWidth: 140, flex: 1, maxWidth: 200 }}>
          <Search size={18} />
          <input type="text" placeholder="No Body" value={filterNoBody} onChange={e => setFilterNoBody(e.target.value)} />
        </div>
        <div className="search-box" style={{ minWidth: 140, flex: 1, maxWidth: 200 }}>
          <Search size={18} />
          <input type="text" placeholder="No Polisi" value={filterNoPolisi} onChange={e => setFilterNoPolisi(e.target.value)} />
        </div>
        <div className="search-box" style={{ minWidth: 140, flex: 1, maxWidth: 200 }}>
          <Search size={18} />
          <input type="text" placeholder="Teknisi" value={filterTeknisi} onChange={e => setFilterTeknisi(e.target.value)} />
        </div>
        <div className="search-box" style={{ minWidth: 140, flex: 1, maxWidth: 200 }}>
          <MapPin size={18} style={{ color: 'var(--gray-400)' }} />
          <input type="text" placeholder="Lokasi Pool" value={filterLokasi} onChange={e => setFilterLokasi(e.target.value)} />
        </div>
        <div className="search-box" style={{ minWidth: 140, flex: 1, maxWidth: 200 }}>
          <Search size={18} style={{ color: 'var(--gray-400)' }} />
          <input type="text" placeholder="Nama Klien" value={filterNamaKlien} onChange={e => setFilterNamaKlien(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Jenis:</span>
          <select
            value={filterJenisKendaraan}
            onChange={e => setFilterJenisKendaraan(e.target.value)}
            style={{
              padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8,
              fontSize: '0.82rem', background: '#fff', maxWidth: 130,
            }}
          >
            <option value="">Semua</option>
            <option value="AC">❄ AC</option>
            <option value="Non AC">🔧 Non AC</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={16} className="text-muted" />
          <input type="date" value={filterTglAwal}
            onChange={e => setFilterTglAwal(e.target.value)}
            style={{
              padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8,
              fontSize: '0.82rem', background: '#fff', maxWidth: 150,
            }} />
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>s/d</span>
          <input type="date" value={filterTglAkhir}
            onChange={e => setFilterTglAkhir(e.target.value)}
            style={{
              padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8,
              fontSize: '0.82rem', background: '#fff', maxWidth: 150,
            }} />
        </div>
        {(filterNoBody || filterNoPolisi || filterTeknisi || filterLokasi || filterNamaKlien || filterJenisKendaraan || filterTglAwal || filterTglAkhir) && (
          <button className="btn-icon" title="Reset filter"
            onClick={() => { setFilterNoBody(''); setFilterNoPolisi(''); setFilterTeknisi('');            setFilterLokasi('');
            setFilterNamaKlien('');
            setFilterJenisKendaraan('');
            setFilterTglAwal('');
            setFilterTglAkhir(''); }}
            style={{ color: '#f5365c' }}>
            <X size={16} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="page-loading">Memuat data...</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table pm-table">
            <thead>
              <tr>
                <th style={{ width: 140, textAlign: 'center' }}>No. PM</th>
                <th>Tanggal</th>
                <th>Rute</th>
                <th>No. Body</th>
                <th>No. Polisi</th>
                <th>Nama Klien</th>
                <th>Teknisi</th>
                <th>Keterangan</th>
                <th style={{ width: 60, textAlign: 'center' }}>Foto</th>
                <th style={{ width: 60, textAlign: 'center' }}>CM Status</th>
                <th style={{ width: 120, textAlign: 'center' }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, idx) => {
                const tidakCount = d.checklist_items?.filter(i => i.kondisi === 'Tidak').length || 0;
                return (
                  <tr key={d.id}>
                    <td style={{ textAlign: 'center', fontSize: '0.78rem' }}>{d.pm_id ? <code style={{ fontSize: '0.72rem', padding: '2px 5px', borderRadius: 4, background: '#f0f0f0', fontWeight: 600 }}>{d.pm_id}</code> : idx + 1}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.82rem' }}>{d.tanggal?.slice(0, 10) || '-'}</span>
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>{d.rute || '-'}</td>
                    <td style={{ fontSize: '0.8rem' }}>{d.no_body || '-'}</td>
                    <td style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0a66c2' }}>{d.no_polisi || '-'}</td>
                    <td style={{ fontSize: '0.82rem', fontWeight: 500 }}>{d.nama_klien || '-'}</td>
                    <td style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2dce89' }}>{d.teknisi || '-'}</td>
                    <td style={{ fontSize: '0.82rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.catatan || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(d.foto_urls?.length > 0 || d.video_urls?.length > 0 || d.foto_url || d.video_url) ? (
                        <span style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {[...(d.foto_urls || [d.foto_url].filter(Boolean))].slice(0, 3).map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                              title={`Foto ${i+1}`}
                              style={{ color: '#2dce89', display: 'inline-flex' }}>
                              <Image size={14} />
                            </a>
                          ))}
                          {(d.foto_urls?.length || (d.foto_url ? 1 : 0)) > 3 && (
                            <span style={{ fontSize: '0.6rem', color: 'var(--gray-400)', fontWeight: 600 }}>+{((d.foto_urls?.length || (d.foto_url ? 1 : 0))) - 3}</span>
                          )}
                          {[...(d.video_urls || [d.video_url].filter(Boolean))].slice(0, 2).map((url, i) => (
                            <a key={`v${i}`} href={url} target="_blank" rel="noopener noreferrer"
                              title={`Video ${i+1}`}
                              style={{ color: '#11cdef', display: 'inline-flex' }}>
                              <Film size={14} />
                            </a>
                          ))}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--gray-300)', fontSize: '0.7rem' }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {pmCmStatusMap[d.id] === 'Selesai' ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: '0.62rem', fontWeight: 700, color: '#fff',
                          background: 'linear-gradient(135deg, #2dce89, #1aae6f)',
                          padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
                        }} title="CM untuk PM ini sudah selesai">
                          <CheckCircle size={9} /> CM Selesai
                        </span>
                      ) : pmCmStatusMap[d.id] === 'Diproses' ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: '0.62rem', fontWeight: 700, color: '#fff',
                          background: 'linear-gradient(135deg, #11cdef, #0ea5e9)',
                          padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
                        }} title="CM sedang diproses">
                          <Clock size={9} /> CM Diproses
                        </span>
                      ) : null}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="action-btns" style={{ justifyContent: 'center' }}>
                        {tidakCount > 0 && hasMenuAction('cm', 'create') && (
                          <button className="btn-icon" title="Ajukan CM - Ada komponen tidak baik"
                            onClick={() => {
                              const rusakItems = (d.checklist_items || []).filter(i => i.kondisi === 'Tidak');
                              let catatanText = d.catatan || '';
                              if (rusakItems.length > 0) {
                                const summary = '\n[Temuan dari PM]:\n' + rusakItems.map((i, idx) => `${idx+1}. ${i.nama}: ${i.kondisi}${i.keterangan ? ' - ' + i.keterangan : ''}`).join('\n');
                                catatanText = catatanText + summary;
                              }
                              window.open(`/cm?from_pm=${d.id}&pm_id=${d.id}&teknisi=${encodeURIComponent(d.teknisi)}&polisi=${encodeURIComponent(d.no_polisi)}&body=${encodeURIComponent(d.no_body)}&pengawas=${encodeURIComponent(d.pengawas)}&vehicle_id=${d.vehicle_id||''}&catatan=${encodeURIComponent(catatanText)}`, '_self');
                            }}
                            style={{
                              background: '#fff5f5', border: '1px solid #fecaca',
                              borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
                              color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#fff5f5'; e.currentTarget.style.borderColor = '#fecaca'; }}>
                            <Lock size={12} />
                            CM
                            <ExternalLink size={10} />
                          </button>
                        )}
                        {hasMenuAction('pm', 'edit') && (
                          <button className="btn-icon" title="Edit" onClick={() => setModalState({ mode: 'edit', item: d })} style={{ color: '#2dce89' }}>
                            <Edit2 size={13} />
                          </button>
                        )}
                        <button className="btn-icon" title="Cetak" onClick={() => handleCetak(d)}
                          style={{ color: '#11cdef' }}>
                          <Printer size={13} />
                        </button>
                        {hasMenuAction('pm', 'delete') && (
                          <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gray-400)' }}>
                    Tidak ada data PM
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalState && (
        <PMModal
          editing={modalState.mode === 'edit' ? modalState.item : null}
          teknisiPenugasan={teknisiPenugasan}
          penugasanData={penugasanData}
          vehicles={vehicles}
          allPMData={data}
          user={user}
          onClose={() => setModalState(null)}
          onSaved={() => { setModalState(null); load(); }}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
